import { Component, Input, OnChanges, OnInit } from '@angular/core';
import { StatsRepoService } from '@client/app/services/api/stats-repo/stats-repo.service';
import { SessionService } from '@monsido/core/session/session.service';
import { PipeService } from '@client/app/filters/pipe-transformation.service';
import { StateService } from '@uirouter/core';
import moment from 'moment';
import { BaseChart } from '@client/app/modules/charts/models/chart';
import { DateTimeService } from '@client/app/services/date-time/date-time.service';
import { TranslateService } from '@client/app/services/translate/translate.service';

export interface TrafficSourceSummary {
    name: string;
    visits: number;
    percentage: number;
    change: number;
    changeType: 'increase' | 'decrease' | 'neutral';
}

export interface StatsSummaryTrafficSourcesData {
    totalTrafficSources: number;
    topSources: TrafficSourceSummary[];
    directTraffic: number;
    searchEngines: number;
    socialMedia: number;
    referrals: number;
    others: number;
}

@Component({
    selector: 'statistics-summary-traffic-sources',
    templateUrl: './traffic-sources.component.html',
    standalone: false,
})
export class StatisticsSummaryTrafficSourcesComponent implements OnInit, OnChanges {
    @Input() public startDate: any = moment();
    @Input() public endDate: any = moment();

    public summary: StatsSummaryTrafficSourcesData = {
        totalTrafficSources: 0,
        topSources: [],
        directTraffic: 0,
        searchEngines: 0,
        socialMedia: 0,
        referrals: 0,
        others: 0,
    };

    public loading = false;
    remoteData = {};
    counts: any = {
        total: 0,
    };
    lineChartLabels: any[] = [];
    lineChartOverride: any[] = [];
    lineChartSeries: any[] = [];
    lineChartColors: any[] = [];
    lineChartData: any[] = [];
    chart: any;

    constructor (
        private statsRepoService: StatsRepoService,
        public sessionService: SessionService,
        public pipeService: PipeService,
        private stateService: StateService,
        private dateTimeService: DateTimeService,
        private translateService: TranslateService,
    ) {}

    ngOnInit (): void {
        this.setupChartSettings();
    }

    ngOnChanges (changes) {
        if (changes === undefined) {
            return;
        }
        this.setupChart();
    }

    setupChartSettings () {
        this.lineChartLabels = [];
        this.lineChartOverride = [];
        this.lineChartSeries = [
            this.translateService.getString('Search engine'),
            this.translateService.getString('Direct visits'),
            this.translateService.getString('Referring visits'),
            this.translateService.getString('Social visits'),
            this.translateService.getString('Campaign visits'),
        ];
        this.lineChartColors = ['#71a3f6', '#57B754', '#EAA646', '#9500AC', '#D24747'];
        this.chart = new BaseChart();
        this.chart.setTooltipCustom({
            mode: 'label',
            intersect: false,
            callbacks: {
                label: (tooltipItem, data) => (data.datasets[tooltipItem.datasetIndex].label + ': ' +
                        this.pipeService.get('monNumber')(data.datasets[tooltipItem.datasetIndex].data[tooltipItem.index])),
            },
        });
        this.chart.setScalesSettings({
            yAxes: [
                {
                    id: 'y-axis-0',
                    type: 'linear',
                    position: 'right',
                    ticks: {
                        beginAtZero: true,
                        suggestedMax: 4,
                        maxTicksLimit: 5,
                        callback: (value) => this.pipeService.get('monNumber')(value, '0,0'),
                        min: 0,
                    },
                    stacked: true,
                },
            ],
            xAxes: [
                {
                    gridLines: {
                        display: false,
                    },
                    stacked: true,
                    ticks: {
                        callback: (value) => {
                            if (value) {
                                return this.dateTimeService.format(this.dateTimeService.toUtcDate(value, 'll'), 'MMM DD');
                            }
                            return '';
                        },
                    },
                },
            ],
        });

        for (let i = 0; i < this.lineChartSeries.length; i++) {
            this.lineChartOverride.push({
                type: 'bar',
                fill: false,
                borderColor: this.lineChartColors[i],
                borderWidth: 1,
            });
        }
    }

    setupChart () {
        const promises: any[] = [];
        const params = {
            from: this.startDate.format('YYYY-MM-DD'),
            to: this.endDate.format('YYYY-MM-DD'),
        };
        const types = ['organic', 'direct', 'referring', 'social', 'paid'];
        for (let j = 0; j < types.length; j++) {
            promises.push(this.getVisitorsForType(types[j], params));
        }

        this.loading = true;
        Promise.all(promises)
            .then(() => {
                this.lineChartData = [];
                const typeWithMostData = types.reduce((primaryType, type) => {
                    if (this.remoteData[type].length < this.remoteData[primaryType].length) {
                        return type;
                    }
                    return primaryType;
                }, 'organic');

                this.counts.total = 0;

                types.forEach((type) => {
                    this.lineChartData.push(
                        this.remoteData[type].map((typeData) => {
                            return +typeData.visits || 0;
                        }),
                    );
                    this.counts[type] = this.lineChartData[this.lineChartData.length - 1].reduce((num, currentValue) => num + currentValue, 0);
                    this.counts.total += this.counts[type];
                });

                this.lineChartLabels = this.remoteData[typeWithMostData].map(
                    (typeData) => this.dateTimeService.format(this.dateTimeService.toUtcDate(typeData.date), 'll'),
                );
            }, () => {})
            .finally(() => {
                this.loading = false;
            });
    }

    getVisitorsForType (type, params) {
        return this.statsRepoService.getVisitors({ ...params, type: type }).then(
            (data) => {
                this.remoteData[type] = data;
            },
            () => {
                this.remoteData[type] = [];
            },
        );
    }
}
